import { all, put, takeLatest } from "redux-saga/effects";

import {
  COMPLETE_APP_ONBOARDING_STEP,
  COMPLETE_APP_ONBOARDING_STEP_FAILURE,
  COMPLETE_APP_ONBOARDING_STEP_SUCCESS,
  FETCH_APP_ONBOARDING,
  FETCH_APP_ONBOARDING_FAILURE,
  FETCH_APP_ONBOARDING_SUCCESS,
  SKIP_APP_ONBOARDING_STEP,
  SKIP_APP_ONBOARDING_STEP_FAILURE,
  SKIP_APP_ONBOARDING_STEP_SUCCESS,
  POLL_APP_ONBOARDING_PROGRESS,
  POLL_APP_ONBOARDING_PROGRESS_SUCCESS,
  POLL_APP_ONBOARDING_PROGRESS_FAILURE,
  UPDATE_DISCOUNT_MODAL_SHOWN,  UPDATE_DISCOUNT_USED_STATUS,
  UPDATE_DISCOUNT_USED_STATUS_SUCCESS,
  UPDATE_DISCOUNT_USED_STATUS_FAILURE,  FETCH_ONBOARDING_FEATURES,
  FETCH_ONBOARDING_FEATURES_SUCCESS,
  FETCH_ONBOARDING_FEATURES_FAILURE,
  DISMISS_ONBOARDING_FEATURE,
  DISMISS_ONBOARDING_FEATURE_SUCCESS,
  DISMISS_ONBOARDING_FEATURE_FAILURE,
} from "../../modules/constants";

import { defaultApi } from "../../utils/axiosApi";

// ---------------------------------------------------------------------------
// Workers
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------

export function* fetchAppOnboarding({ payload: { organizationId } }) {
  try {
    // Including organization_id in payload as requested
    const response = yield defaultApi(`/onboarding/${organizationId}/progress/`, "GET", { organization_id: organizationId });
    yield put({
      type: FETCH_APP_ONBOARDING_SUCCESS,
      payload: {
        steps: response.data.steps,
        trialConfig: {
          durationHours: response.data.duration_hours,
          creditAmount: response.data.credit_amount,
          creditCurrency: response.data.credit_currency,
          trialStartedAt: response.data.created_at,
        },
        discountModalShown: response.data.discount_modal_shown,
        isCompleted: response.data.is_completed,
        discountEarned: response.data.discount_earned,
        discountCode: response.data.paddle_discount_id,
        paddleDiscountId: response.data.paddle_discount_id,
        discountExpiresAt: response.data.discount_expires_at,
        discountUsed: response.data.discount_used,
      },
    });
  } catch (err) {
    yield put({ type: FETCH_APP_ONBOARDING_FAILURE, payload: err });
  }
}

export function* pollAppOnboardingProgress({ payload: { organizationId } }) {
  try {
    const response = yield defaultApi(`/onboarding/${organizationId}/progress/`, "GET", { organization_id: organizationId });
    yield put({
      type: POLL_APP_ONBOARDING_PROGRESS_SUCCESS,
      payload: {
        steps: response.data.steps,
        trialConfig: {
          durationHours: response.data.duration_hours,
          creditAmount: response.data.credit_amount,
          creditCurrency: response.data.credit_currency,
          trialStartedAt: response.data.created_at,
        },
        discountModalShown: response.data.discount_modal_shown,
        isCompleted: response.data.is_completed,
        discountEarned: response.data.discount_earned,
        discountCode: response.data.paddle_discount_id,
        paddleDiscountId: response.data.paddle_discount_id,
        discountExpiresAt: response.data.discount_expires_at,
        discountUsed: response.data.discount_used,
      },
    });
  } catch (err) {
    yield put({ type: POLL_APP_ONBOARDING_PROGRESS_FAILURE, payload: err });
  }
}

export function* completeAppOnboardingStep({ payload: { organizationId, stepId } }) {
  try {
    const endpoint = `/onboarding/${organizationId}/complete/`;
    const payload = {
      step: stepId,
    };
    yield defaultApi(endpoint, "POST", payload);

    // Pass stepId so reducer can locally mark the step as completed without a full refetch
    yield put({ type: COMPLETE_APP_ONBOARDING_STEP_SUCCESS, payload: { stepId } });
    // Refresh onboarding progress immediately so isCompleted/discountEarned are updated
    yield put({ type: FETCH_APP_ONBOARDING, payload: { organizationId } });
  } catch (err) {
    yield put({ type: COMPLETE_APP_ONBOARDING_STEP_FAILURE, payload: err });
  }
}

export function* skipAppOnboardingStep({ payload: { organizationId, stepId } }) {
  try {
    const endpoint = `/onboarding/${organizationId}/skip/`;
    const payload = {
      step: stepId,
    };
    yield defaultApi(endpoint, "POST", payload);

    yield put({ type: SKIP_APP_ONBOARDING_STEP_SUCCESS, payload: { stepId } });
    // Re-fetch to get the latest completion state from the server
    // This will now be a "silent" update because steps are already in Redux
    yield put({ type: FETCH_APP_ONBOARDING, payload: { organizationId } });
  } catch (err) {
    yield put({ type: SKIP_APP_ONBOARDING_STEP_FAILURE, payload: { error: err, stepId } });
  }
}


export function* updateDiscountModalShown({ payload: { organizationId } }) {
  if (!organizationId) {
    console.warn("AppOnboarding Saga: updateDiscountModalShown missing organizationId");
    return;
  }

  try {
    const endpoint = `/onboarding/${organizationId}/progress/`;
    yield defaultApi(endpoint, "PATCH", { discount_modal_shown: true });
  } catch (err) {
    console.error("AppOnboarding Saga: Failed to update discount_modal_shown status.", err);
  }
}

export function* updateDiscountUsedStatus({ payload: { organizationId, discountUsed } }) {
  if (!organizationId) {
    console.warn("AppOnboarding Saga: updateDiscountUsedStatus missing organizationId");
    return;
  }

  try {
    const endpoint = `/onboarding/${organizationId}/progress/`;
    yield defaultApi(endpoint, "PATCH", { discount_used: discountUsed });
    yield put({ type: UPDATE_DISCOUNT_USED_STATUS_SUCCESS, payload: { discountUsed } });
  } catch (err) {
    yield put({ type: UPDATE_DISCOUNT_USED_STATUS_FAILURE, payload: err });
  }
}

export function* fetchOnboardingFeatures({ payload: { organizationId } }) {
  try {
    const response = yield defaultApi(`/onboarding/${organizationId}/features/`, "GET");
    yield put({
      type: FETCH_ONBOARDING_FEATURES_SUCCESS,
      payload: {
        features: {
          has_customer: response.data.has_customer,
          has_job: response.data.has_job,
          has_schedule: response.data.has_schedule,
          has_invoice: response.data.has_invoice,
          has_team_member: response.data.has_team_member,
          has_route_optimization: response.data.has_route_optimization,
          has_attendance: response.data.has_attendance,
          has_timesheet: response.data.has_timesheet,
          has_estimate: response.data.has_estimate,
          has_payment: response.data.has_payment,
          dismissed_videos: response.data.dismissed_videos || [],
        },
        videosToShow: response.data.videos_to_show || [],
      },
    });
  } catch (err) {
    yield put({ type: FETCH_ONBOARDING_FEATURES_FAILURE, payload: err });
  }
}

export function* dismissOnboardingFeature({ payload: { organizationId, featureName } }) {
  try {
    console.log("Dismiss saga started", { organizationId, featureName });
    const response = yield defaultApi(
      `/onboarding/${organizationId}/features/dismiss/`,
      'POST',
      { feature: featureName }
    );
    console.log("Dismiss API response", response.data);
    yield put({
      type: DISMISS_ONBOARDING_FEATURE_SUCCESS,
      payload: { featureName, dismissed_videos: response.data.dismissed_videos },
    });
    console.log("Dismiss success action dispatched", { featureName, dismissed_videos: response.data.dismissed_videos });
  } catch (err) {
    console.error("Dismiss API error", err);
    yield put({ type: DISMISS_ONBOARDING_FEATURE_FAILURE, payload: err });
  }
}

// ---------------------------------------------------------------------------
// Root watcher
// ---------------------------------------------------------------------------

export default function* appOnboardingSagas() {
  yield all([
    takeLatest(FETCH_APP_ONBOARDING, fetchAppOnboarding),
    takeLatest(POLL_APP_ONBOARDING_PROGRESS, pollAppOnboardingProgress),
    takeLatest(COMPLETE_APP_ONBOARDING_STEP, completeAppOnboardingStep),
    takeLatest(SKIP_APP_ONBOARDING_STEP, skipAppOnboardingStep),
    takeLatest(UPDATE_DISCOUNT_MODAL_SHOWN, updateDiscountModalShown),
    takeLatest(UPDATE_DISCOUNT_USED_STATUS, updateDiscountUsedStatus),
    takeLatest(FETCH_ONBOARDING_FEATURES, fetchOnboardingFeatures),
    takeLatest(DISMISS_ONBOARDING_FEATURE, dismissOnboardingFeature),
  ]);
}